import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from './DataConstants';
import { floatToPercentageString } from './DataFormatterUtils';
import { nullSafeGetOrElse } from './DataAccessUtils';
import { COMPARISON_MODE, ComparisonState } from './AnalyticsComparisonUtils';
import { SpendSourceDescriptor } from './AnalyticsUtils';

export type BreakdownExportRow = {
	fieldName: string;
	subTotal: number;
	percentageOfTotal?: number;
};

export type ExportBreakdownCsvOptions = {
	title: string;
	categoryColumnLabel: string;
	valueColumnLabel: string;
	currentRows: BreakdownExportRow[];
	comparisonRows?: BreakdownExportRow[];
	comparisonState?: ComparisonState | null;
	formatValue: (amount: number) => string | number;
};

export const exportBreakdownCsv = ({
	title,
	categoryColumnLabel,
	valueColumnLabel,
	currentRows,
	comparisonRows,
	comparisonState,
	formatValue,
}: ExportBreakdownCsvOptions) => {
	const hasComparison =
		comparisonState &&
		comparisonState.mode !== COMPARISON_MODE.NONE &&
		comparisonRows &&
		comparisonRows.length > 0;

	const baseHeaders = [categoryColumnLabel, valueColumnLabel, 'Percentage'];
	const headers = hasComparison ? [...baseHeaders, 'Historical Data'] : baseHeaders;

	const mapRow = (row: BreakdownExportRow, isHistorical: boolean) => {
		const base: Record<string, string | number> = {
			category: row.fieldName,
			value: formatValue(nullSafeGetOrElse('subTotal', row, 0)),
			percentage: floatToPercentageString(nullSafeGetOrElse('percentageOfTotal', row, 0) / 100),
		};
		if (hasComparison) {
			base.historicalData = isHistorical ? 'Yes' : 'No';
		}
		return base;
	};

	const previousRows = hasComparison ? comparisonRows.map((row) => mapRow(row, true)) : [];
	const currentExportRows = (currentRows || []).map((row) => mapRow(row, false));

	const csvExporter = new ExportToCsv({
		...CSV_EXPORT_DEFAULTS,
		filename: title,
		title,
		headers,
	});

	csvExporter.generateCsv([...previousRows, ...currentExportRows]);
};

export type ExportSpendBySourceCsvOptions = {
	title: string;
	categoryColumnLabel: string;
	// SpendCostsByField rows (string amounts), already flattened from any tree.
	rows: any[];
	activeSources: SpendSourceDescriptor[];
	formatValue: (amount: number) => string | number;
};

// CSV export for the per-source spend tables (OPE-4875): category, one column per active source,
// Total, and % of total. Only the sources visible in the table (activeSources) are emitted.
export const exportSpendBySourceCsv = ({
	title,
	categoryColumnLabel,
	rows,
	activeSources,
	formatValue,
}: ExportSpendBySourceCsvOptions) => {
	const headers = [
		categoryColumnLabel,
		...activeSources.map((source) => source.label),
		'Total',
		'Percentage',
	];

	// Total and % are derived from the *shown* sources, so an invoice-only tab (Suppliers) doesn't
	// leak parts/tools/spot-purchase spend into the Total or skew the percentage.
	const rowTotal = (row) =>
		activeSources.reduce(
			(sum, source) => sum + (parseFloat(nullSafeGetOrElse(source.key, row, 0)) || 0),
			0
		);
	const grandTotal = (rows || []).reduce((sum, row) => sum + rowTotal(row), 0);

	const exportRows = (rows || []).map((row) => {
		const mapped: Record<string, string | number> = {
			category: nullSafeGetOrElse('fieldName', row, ''),
		};
		activeSources.forEach((source) => {
			mapped[source.key] = formatValue(parseFloat(nullSafeGetOrElse(source.key, row, 0)) || 0);
		});
		const total = rowTotal(row);
		mapped.total = formatValue(total);
		mapped.percentage = floatToPercentageString(grandTotal > 0 ? total / grandTotal : 0);
		return mapped;
	});

	const csvExporter = new ExportToCsv({
		...CSV_EXPORT_DEFAULTS,
		filename: title,
		title,
		headers,
	});

	csvExporter.generateCsv(exportRows);
};

export type MonthlySeriesCsvOptions = {
	filename: string;
	/** Header for the value column; also used as the CSV title. */
	valueHeader: string;
	currentRows: any[];
	previousRows: any[];
	includeComparison: boolean;
	formatValue: (row: any) => string | number;
};

export const exportMonthlySeriesCsv = ({
	filename,
	valueHeader,
	currentRows,
	previousRows,
	includeComparison,
	formatValue,
}: MonthlySeriesCsvOptions) => {
	if (!currentRows || currentRows.length === 0) return;
	const baseHeaders = ['Month', valueHeader];
	const headers = includeComparison ? [...baseHeaders, 'Historical Data'] : baseHeaders;

	const mapRow = (row: any, isHistorical: boolean): Record<string, string | number> => {
		const base: Record<string, string | number> = {
			month: row.monthFull || row.monthLabel || '',
			value: formatValue(row),
		};
		if (includeComparison) {
			base.historicalData = isHistorical ? 'Yes' : 'No';
		}
		return base;
	};

	const previousExportRows = includeComparison
		? (previousRows || []).map((row: any) => mapRow(row, true))
		: [];
	const currentExportRows = currentRows.map((row: any) => mapRow(row, false));

	const csvExporter = new ExportToCsv({
		...CSV_EXPORT_DEFAULTS,
		filename,
		title: valueHeader,
		headers,
	});
	csvExporter.generateCsv([...previousExportRows, ...currentExportRows]);
};
